import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController, NavParams, ViewController } from 'ionic-angular';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { DailyProgressReportService } from '../../../services/daily-progress-report/daily-progress-report.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { VesselService } from '../../../services/vessel/vessel.service';

@Component({
  selector: 'app-signature-date',
  styles: ['signature-date-modal.component.scss'],
  templateUrl: './signature-date-modal.component.html',
})
export class SignatureDateModalComponent implements OnInit, AfterViewInit, OnDestroy {
  config = AppConfig.settings;
  signatureDate: DateTime;
  isSignOff: boolean;
  dailyLogDate: any; // PtLuxonDate
  dailyLogDateNoOffset: DateTime;
  maxDate: DateTime;
  loading = false;
  showDateError = false;
  clientComments = '';
  ptLuxonDate: PtLuxonDate;
  currentVesselId: number;
  previewLoading: boolean;
  pdfPreviewUrl: string;

  @ViewChild('masterCanvas') masterCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('clientCanvas') clientCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('otherCanvas') otherCanvasRef: ElementRef<HTMLCanvasElement>;

  private pads: { [key: string]: SignaturePadShim } = {};
  private padsInited = false;

  constructor(
    params: NavParams,
    public viewController: ViewController,
    public modalController: ModalController,
    private dprService: DailyProgressReportService,
    private dailyLogService: DailyLogService,
    private cdr: ChangeDetectorRef,
    private vesselService: VesselService,
  ) {
    this.isSignOff = params.get('isSignOff');
    this.dailyLogDate = params.get('dailyLogDate');
    const systemNow = new Date();
    this.maxDate = DateTime.fromObject({
      year: systemNow.getFullYear(),
      month: systemNow.getMonth() + 1,
      day: systemNow.getDate(),
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 999
    });
    this.dailyLogDateNoOffset = this.dailyLogDate.dateTime.setZone('utc', { keepLocalTime: true });
  }

  async ngOnInit() {
    if (this.isSignOff) {
      await this.loadCurrentSignatureDate();
    }
  }

  ngAfterViewInit() {
    this.initPadsIfReady();
  }

  ngOnDestroy() {
    Object.keys(this.pads).forEach(role => this.pads[role].destroy());
  }

  async ionViewDidLoad() {
    this.ptLuxonDate = await this.dailyLogService.getCurrentLogDay();

    this.previewLoading = true;

    this.vesselService.getEditor().then(async editor => {
      this.currentVesselId = editor.id;

      await this.loadPreviewViewer();
    });
  }

  async loadPreviewViewer() {
    this.pdfPreviewUrl = this.dprService.getPreviewDprDownloadLink(this.ptLuxonDate, this.currentVesselId, this.config.isImplementerEnvironment);
  }

  previewLoaded() {
    this.previewLoading = false;
  }

  private initPadsIfReady() {
    if (this.padsInited) return;
    // Only init if elements exist (i.e., loading is false and *ngIf rendered them)
    if (this.masterCanvasRef && this.clientCanvasRef && this.otherCanvasRef) {
      this.pads['Master'] = new SignaturePadShim(this.masterCanvasRef.nativeElement);
      this.pads['Client'] = new SignaturePadShim(this.clientCanvasRef.nativeElement);
      this.pads['Other']  = new SignaturePadShim(this.otherCanvasRef.nativeElement);
      this.padsInited = true;
    }
  }

  private async loadCurrentSignatureDate() {
    this.loading = true;
    this.dprService.getCurrentSignatureDateForDay(this.dailyLogDate).then(signatureDate => {
      this.signatureDate = signatureDate;
      this.loading = false;

      // force a change detection cycle so ViewChilds are wired after *ngIf flips
      this.cdr.detectChanges();

      // let the view paint, then init pads
      setTimeout(() => this.initPadsIfReady());
    });
  }

  async dateChanged($event: DateTime) {
    this.signatureDate = $event;
  }

  clear(role: 'Master' | 'Client' | 'Other') {
    this.pads[role]?.clear();
  }

  confirm() {
    this.showDateError = false;
    if (this.signatureDate > this.maxDate) {
      this.showDateError = true;
      return;
    }

    const masterPng = this.pads['Master'].toDataUrl();
    const clientPng = this.pads['Client'].toDataUrl();
    const otherPng  = this.pads['Other'].toDataUrl();

    // return everything to caller; server save can happen there or here
    this.viewController.dismiss({
      confirmed: true,
      signatureDate: this.signatureDate,
      clientComments: this.clientComments,
      signatures: {
        Master: masterPng,
        Client: clientPng,
        Other: otherPng
      }
    });
  }

  closeModal(choice: boolean) {
    this.viewController.dismiss({ confirmed: choice });
  }
}

class SignaturePadShim {
  private ctx: CanvasRenderingContext2D;
  private drawing = false;
  private hasSignature = false;
  private strokes: SignaturePoint[][] = [];
  private activeStroke: SignaturePoint[] | null = null;
  private readonly onMouseDown: (e: MouseEvent) => void;
  private readonly onMouseMove: (e: MouseEvent) => void;
  private readonly onMouseUp: () => void;
  private readonly onTouchStart: (e: TouchEvent) => void;
  private readonly onTouchMove: (e: TouchEvent) => void;
  private readonly onTouchEnd: () => void;
  private readonly onResize: () => void;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    this.ctx = ctx;
    this.applyDrawingStyles();

    this.onMouseDown = (e: MouseEvent) => this.startStroke(e);
    this.onMouseMove = (e: MouseEvent) => this.extendStroke(e);
    this.onMouseUp = () => this.endStroke();
    this.onTouchStart = (e: TouchEvent) => this.startStroke(e);
    this.onTouchMove = (e: TouchEvent) => this.extendStroke(e);
    this.onTouchEnd = () => this.endStroke();
    this.onResize = () => this.resizeToDevicePixelRatio();

    // mouse
    canvas.addEventListener('mousedown', this.onMouseDown, false);
    canvas.addEventListener('mousemove', this.onMouseMove, false);
    window.addEventListener('mouseup', this.onMouseUp, false);

    // touch
    canvas.addEventListener('touchstart', this.onTouchStart as any, { passive: false } as any);
    canvas.addEventListener('touchmove', this.onTouchMove as any, { passive: false } as any);
    window.addEventListener('touchend', this.onTouchEnd as any, false);

    // handle resizing: keep high-dpi sharpness
    this.resizeToDevicePixelRatio();
    window.addEventListener('resize', this.onResize, false);
  }

  clear() {
    this.strokes = [];
    this.activeStroke = null;
    this.hasSignature = false;
    this.clearCanvas();
  }

  toDataUrl(): string | null {
    if (!this.hasSignature) {
      return null;
    }

    return this.canvas.toDataURL('image/png');
  }

  destroy() {
    this.canvas.removeEventListener('mousedown', this.onMouseDown, false);
    this.canvas.removeEventListener('mousemove', this.onMouseMove, false);
    window.removeEventListener('mouseup', this.onMouseUp, false);
    this.canvas.removeEventListener('touchstart', this.onTouchStart as any, false);
    this.canvas.removeEventListener('touchmove', this.onTouchMove as any, false);
    window.removeEventListener('touchend', this.onTouchEnd as any, false);
    window.removeEventListener('resize', this.onResize, false);
  }

  private getPos(e: MouseEvent | TouchEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const t: any = (e as TouchEvent).touches && (e as TouchEvent).touches[0] ? (e as TouchEvent).touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  }

  private resizeToDevicePixelRatio() {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const cssWidth = this.canvas.clientWidth;
    const cssHeight = this.canvas.clientHeight;

    if (!cssWidth || !cssHeight) {
      return;
    }

    this.canvas.width = Math.floor(cssWidth * ratio);
    this.canvas.height = Math.floor(cssHeight * ratio);
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    this.applyDrawingStyles();
    this.redraw();
  }

  private applyDrawingStyles() {
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.strokeStyle = '#000';
  }

  private startStroke(e: MouseEvent | TouchEvent) {
    this.drawing = true;
    this.hasSignature = true;
    const point = this.getNormalisedPoint(this.getPos(e));
    this.activeStroke = [point];
    this.strokes.push(this.activeStroke);
    this.redraw();
    e.preventDefault();
  }

  private extendStroke(e: MouseEvent | TouchEvent) {
    if (!this.drawing || !this.activeStroke) {
      return;
    }

    this.activeStroke.push(this.getNormalisedPoint(this.getPos(e)));
    this.redraw();
    e.preventDefault();
  }

  private endStroke() {
    this.drawing = false;
    this.activeStroke = null;
  }

  private getNormalisedPoint(point: { x: number; y: number }): SignaturePoint {
    const width = this.canvas.clientWidth || 1;
    const height = this.canvas.clientHeight || 1;

    return {
      x: point.x / width,
      y: point.y / height
    };
  }

  private redraw() {
    this.clearCanvas();

    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;

    this.strokes.forEach(stroke => {
      if (!stroke.length) {
        return;
      }

      const firstPoint = this.scalePoint(stroke[0], width, height);
      this.ctx.beginPath();
      this.ctx.moveTo(firstPoint.x, firstPoint.y);

      if (stroke.length === 1) {
        this.ctx.lineTo(firstPoint.x + 0.01, firstPoint.y + 0.01);
      } else {
        stroke.slice(1).forEach(point => {
          const scaled = this.scalePoint(point, width, height);
          this.ctx.lineTo(scaled.x, scaled.y);
        });
      }

      this.ctx.stroke();
    });
  }

  private clearCanvas() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private scalePoint(point: SignaturePoint, width: number, height: number): { x: number; y: number } {
    return {
      x: point.x * width,
      y: point.y * height
    };
  }
}

interface SignaturePoint {
  x: number;
  y: number;
}



// WEBPACK FOOTER //
// ./src/pages/daily-progress-report/signature-date-modal/signature-date-modal.component.ts